/**
 * Theme store — toggles `data-theme` on <html>, persists to localStorage.
 *
 * Defaults to dark; respects `prefers-color-scheme: light` only on first
 * visit (when there's no stored preference yet).
 */

import { useSyncExternalStore } from 'react';

export type Theme = 'dark' | 'light';
const KEY = 'playground:theme:v1';

function initialTheme(): Theme {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    /* ignore */
  }
  if (typeof window !== 'undefined' && window.matchMedia) {
    if (window.matchMedia('(prefers-color-scheme: light)').matches) return 'light';
  }
  return 'dark';
}

let current: Theme = initialTheme();
const listeners = new Set<() => void>();

function apply(t: Theme) {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', t);
  }
}

// Apply on module load so first render matches stored preference
apply(current);

export function setTheme(t: Theme) {
  current = t;
  apply(t);
  try {
    localStorage.setItem(KEY, t);
  } catch {
    /* ignore */
  }
  for (const l of listeners) l();
}

export function toggleTheme() {
  setTheme(current === 'dark' ? 'light' : 'dark');
}

export function useTheme(): Theme {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => current,
    () => current
  );
}
